import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useProjectStore } from '../../stores/useProjectStore';
import { FileExplorer } from '../explorer/FileExplorer';
import { EditorArea } from '../editor/EditorArea';
import { PviGraphPanel } from '../pvi/PviGraphPanel';
import s from './Pages.module.css';

const MIN_SIDEBAR_WIDTH = 160;
const MAX_SIDEBAR_WIDTH = 500;
const DEFAULT_SIDEBAR_WIDTH = 260;

export function IDEWorkspace() {
  const { projectId } = useParams<{ projectId: string }>();
  const openProject = useProjectStore(p => p.openProject);
  const activeProjectId = useProjectStore(p => p.activeProjectId);

  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const resizingSidebar = useRef(false);
  const sidebarStartX = useRef(0);
  const sidebarStartW = useRef(0);

  const onSidebarResizeStart = useCallback((e: React.MouseEvent) => {
    resizingSidebar.current = true;
    sidebarStartX.current = e.clientX;
    sidebarStartW.current = sidebarWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, [sidebarWidth]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizingSidebar.current) return;
      const delta = e.clientX - sidebarStartX.current;
      const newWidth = Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, sidebarStartW.current + delta));
      setSidebarWidth(newWidth);
    };
    const onMouseUp = () => {
      if (resizingSidebar.current) {
        resizingSidebar.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (projectId && projectId !== activeProjectId) {
      openProject(projectId);
    }
  }, [projectId, activeProjectId, openProject]);

  return (
    <div className={s.pageWide} style={{ display: 'flex' }}>
      {/* File explorer sidebar for IDE */}
      <div style={{
        width: sidebarWidth,
        minWidth: MIN_SIDEBAR_WIDTH,
        borderRight: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        <div style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--color-border)',
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--color-text-secondary)',
        }}>
          Explorer
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <FileExplorer />
        </div>
      </div>

      {/* Sidebar resize handle */}
      <div
        onMouseDown={onSidebarResizeStart}
        style={{
          width: 4,
          flexShrink: 0,
          cursor: 'col-resize',
          background: 'transparent',
          transition: 'background 0.15s',
          zIndex: 10,
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--color-accent, #9b59b6)'; }}
        onMouseLeave={(e) => { if (!resizingSidebar.current) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
      />

      {/* Editor area */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <EditorArea />
      </div>

      {/* PVI Graph panel (right) */}
      <PviGraphPanel />
    </div>
  );
}
